/**
 * CouponScanEntry Component (Story 7.3)
 *
 * Container component that provides QR scanning and manual entry options
 * for coupon redemption. Integrates with the backend validation API.
 *
 * Features:
 * - Tab switching between QR scan and manual entry
 * - Validates coupon codes via API
 * - Shows validated coupon details before proceeding
 * - Error handling with localized messages
 *
 * Flow:
 * 1. User scans QR or enters code manually
 * 2. Component validates code via API
 * 3. On success, shows coupon details and calls onCouponValidated
 * 4. On error, shows error message and allows retry
 */

import React, { useState, useCallback } from 'react';
import { QrCode, Keyboard, CheckCircle, AlertCircle, Ticket, User, DollarSign } from 'lucide-react';
import { useTranslation } from '@/i18n';
import QRScanner from './QRScanner';
import ManualCodeEntry from './ManualCodeEntry';
import type { CheckCouponCodeResponseType, CheckCouponCodeBodyType } from '@zlinkx/shared-v2';
import { Numeric } from '@/utils/numeric';

type ValidatedCoupon = NonNullable<CheckCouponCodeResponseType['data']['coupon']>;
type CouponValidationErrorCode = CheckCouponCodeResponseType['data']['error_code'];

/**
 * Props for checking a coupon code
 * Passed from parent that manages the mutation
 */
interface CheckCouponParams {
	merchantId: number;
	data: CheckCouponCodeBodyType;
}

interface CouponScanEntryProps {
	merchantId: number;
	onCouponValidated: (coupon: ValidatedCoupon) => void;
	onCancel?: () => void;
	/**
	 * Callback to check a coupon code. Should be connected to useCheckCouponCode mutation.
	 * Returns the API response with valid flag and coupon data or error code.
	 */
	onCheckCouponCode: (params: CheckCouponParams) => Promise<CheckCouponCodeResponseType['data']>;
	/** Whether a coupon check is currently in progress (from mutation isPending) */
	isCheckingCoupon?: boolean;
}

type EntryMode = 'scan' | 'manual';
type ValidationState = 'idle' | 'validating' | 'success' | 'error';
type LegacyCouponValidationErrorCode = CouponValidationErrorCode | 'ALREADY_REDEEMED';

const CouponScanEntry: React.FC<CouponScanEntryProps> = ({
	merchantId,
	onCouponValidated,
	onCancel,
	onCheckCouponCode,
	isCheckingCoupon = false,
}) => {
	const { t } = useTranslation();
	const [entryMode, setEntryMode] = useState<EntryMode>('scan');
	const [validationState, setValidationState] = useState<ValidationState>('idle');
	const [validatedCoupon, setValidatedCoupon] = useState<ValidatedCoupon | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [showScanner, setShowScanner] = useState(false);
	const isValidating = validationState === 'validating' || isCheckingCoupon;

	// Map API error codes to localized messages
	const getErrorMessage = useCallback(
		(errorCode: LegacyCouponValidationErrorCode): string => {
			switch (errorCode) {
				case 'NOT_FOUND':
					return t('merchant.couponEntry.errors.notFound');
				case 'WRONG_MERCHANT':
					return t('merchant.couponEntry.errors.wrongMerchant');
				case 'ALREADY_REDEEMED':
					return t('merchant.couponEntry.errors.alreadyRedeemed');
				case 'EXPIRED':
					return t('merchant.couponEntry.errors.expired');
				case 'INVALID_FORMAT':
					return t('merchant.couponEntry.errors.invalidFormat');
				case undefined:
					return t('merchant.couponEntry.errors.validationFailed');
			}
		},
		[t]
	);

	// Handle coupon code validation
	const validateCoupon = useCallback(
		async (code: string) => {
			setValidationState('validating');
			setErrorMessage(null);

			try {
				const result = await onCheckCouponCode({
					merchantId,
					data: { code },
				});

				if (result.valid && result.coupon) {
					setValidatedCoupon(result.coupon);
					setValidationState('success');
				} else {
					setErrorMessage(getErrorMessage(result.error_code));
					setValidationState('error');
				}
			} catch (error) {
				console.error('Coupon validation error:', error);
				setErrorMessage(t('merchant.couponEntry.errors.validationFailed'));
				setValidationState('error');
			}
		},
		[merchantId, onCheckCouponCode, getErrorMessage, t]
	);

	// Handle QR scan success
	const handleScanSuccess = useCallback(
		(code: string) => {
			setShowScanner(false);
			void validateCoupon(code);
		},
		[validateCoupon]
	);

	// Handle manual entry submit
	const handleManualSubmit = useCallback(
		(code: string) => {
			void validateCoupon(code);
		},
		[validateCoupon]
	);

	// Handle proceeding with validated coupon
	const handleProceed = useCallback(() => {
		if (validatedCoupon) {
			onCouponValidated(validatedCoupon);
		}
	}, [validatedCoupon, onCouponValidated]);

	// Reset to try again
	const handleTryAgain = useCallback(() => {
		setValidationState('idle');
		setValidatedCoupon(null);
		setErrorMessage(null);
	}, []);

	// Clear error when switching modes or starting new entry
	const clearError = useCallback(() => {
		setErrorMessage(null);
		if (validationState === 'error') {
			setValidationState('idle');
		}
	}, [validationState]);

	// Render success state with coupon details
	if (validationState === 'success' && validatedCoupon) {
		return (
			<div className='bg-white rounded-xl shadow-sm border border-green-200 p-6 sm:p-8'>
				<div className='text-center'>
					{/* Success Icon */}
					<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
						<CheckCircle className='w-10 h-10 text-green-600' />
					</div>
					<h2 className='text-xl sm:text-2xl font-semibold text-gray-900 mb-2'>
						{t('merchant.couponEntry.success.title')}
					</h2>
					<p className='text-gray-600 mb-6'>{t('merchant.couponEntry.success.subtitle')}</p>

					{/* Coupon Details Card */}
					<div className='bg-gray-50 rounded-lg p-4 mb-6 text-left max-w-sm mx-auto'>
						<div className='flex items-start gap-3 mb-3'>
							<Ticket className='w-5 h-5 text-blue-600 mt-0.5' />
							<div>
								<p className='font-semibold text-gray-900'>{validatedCoupon.title}</p>
								<p className='text-sm text-gray-600'>{validatedCoupon.merchantName}</p>
							</div>
						</div>

						<div className='space-y-2 text-sm'>
							{/* Discount Info */}
							<div className='flex items-center gap-2'>
								<DollarSign className='w-4 h-4 text-gray-400' />
								<span className='text-gray-700'>
									{validatedCoupon.couponType === 'percentage'
										? t('common.coupon.percentageOff', { value: validatedCoupon.discountValue ?? 0 })
										: validatedCoupon.couponType === 'fixed_amount'
											? t('common.coupon.amountOff', { value: (validatedCoupon.discountValue ?? 0) / 100 })
											: validatedCoupon.itemDescription ?? validatedCoupon.couponType}
								</span>
							</div>

							{/* Customer Info */}
							{validatedCoupon.customerName && (
								<div className='flex items-center gap-2'>
									<User className='w-4 h-4 text-gray-400' />
									<span className='text-gray-700'>{validatedCoupon.customerName}</span>
								</div>
							)}

							{/* Minimum Purchase */}
							{validatedCoupon.minPurchase && (
								<div className='flex items-center gap-2 text-amber-600'>
									<AlertCircle className='w-4 h-4' />
									<span>
										{t('common.coupon.ordersMinimum', {
											amount: Numeric.formatMoney(validatedCoupon.minPurchase),
										})}
									</span>
								</div>
							)}
						</div>
					</div>

					{/* Action Buttons */}
					<div className='flex flex-col sm:flex-row gap-3 justify-center'>
						<button
							onClick={handleProceed}
							className='px-8 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors'
						>
							{t('merchant.couponEntry.success.proceed')}
						</button>
						<button
							onClick={handleTryAgain}
							className='px-8 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors'
						>
							{t('merchant.couponEntry.success.scanAnother')}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			{/* QR Scanner Modal */}
			{showScanner && (
				<QRScanner
					onScanSuccess={handleScanSuccess}
					onScanError={error => setErrorMessage(error)}
					onClose={() => setShowScanner(false)}
					isProcessing={isValidating}
				/>
			)}

			{/* Main Entry Interface */}
			<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8'>
				{/* Tab Navigation */}
				<div className='flex gap-2 mb-6'>
					<button
						onClick={() => {
							setEntryMode('scan');
							clearError();
						}}
						className={`
							flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors
							${
								entryMode === 'scan'
									? 'bg-blue-50 text-blue-700 border-2 border-blue-200'
									: 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
							}
						`}
					>
						<QrCode className='w-5 h-5' />
						<span>{t('merchant.couponEntry.tabs.scan')}</span>
					</button>
					<button
						onClick={() => {
							setEntryMode('manual');
							clearError();
						}}
						className={`
							flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors
							${
								entryMode === 'manual'
									? 'bg-blue-50 text-blue-700 border-2 border-blue-200'
									: 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
							}
						`}
					>
						<Keyboard className='w-5 h-5' />
						<span>{t('merchant.couponEntry.tabs.manual')}</span>
					</button>
				</div>

				{/* Entry Content */}
				{entryMode === 'scan' ? (
					<div className='text-center'>
						<div className='w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<QrCode className='w-8 h-8 text-blue-600' />
						</div>
						<h2 className='text-xl sm:text-2xl font-semibold text-gray-900 mb-2'>
							{t('merchant.scan.title')}
						</h2>
						<p className='text-gray-600 mb-6'>{t('merchant.scan.subtitle')}</p>

						{/* Error Message */}
						{errorMessage && validationState === 'error' && (
							<div className='flex items-center justify-center gap-2 text-red-600 mb-4'>
								<AlertCircle className='w-5 h-5' />
								<span className='text-sm font-medium'>{errorMessage}</span>
							</div>
							)}

						{/* Validating State */}
						{isValidating && (
							<div className='flex items-center justify-center gap-2 text-blue-600 mb-4'>
								<div className='w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin' />
								<span className='text-sm'>{t('merchant.scan.validating')}</span>
							</div>
						)}

						<button
							onClick={() => setShowScanner(true)}
							disabled={isValidating}
							className='px-8 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
						>
							{t('merchant.scan.openScanner')}
						</button>

						<p className='text-xs text-gray-500 mt-4'>{t('merchant.scan.helpText')}</p>
					</div>
					) : (
						<ManualCodeEntry
							onSubmit={handleManualSubmit}
							isProcessing={isValidating}
							error={validationState === 'error' ? errorMessage : null}
							onClearError={clearError}
						/>
					)}

				{/* Cancel Button */}
				{onCancel && (
					<div className='mt-6 pt-4 border-t border-gray-200 text-center'>
						<button
							onClick={onCancel}
							className='text-gray-500 hover:text-gray-700 text-sm font-medium'
						>
							{t('common.cancel')}
						</button>
					</div>
				)}
			</div>
		</>
	);
};

export default CouponScanEntry;
